import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Heart, Shield, Home, Users, Award, ArrowRight, CheckCircle } from 'lucide-react';

export default function AboutUs() {
  const values = [
    {
      icon: Shield,
      title: 'Healthy Genetics',
      description: 'We carefully select breeding pairs with excellent health records and genetic backgrounds to ensure healthy, happy kittens.'
    },
    {
      icon: Home,
      title: 'Loving Environment',
      description: 'Our kittens are raised in our home, never in cages, with plenty of space to play, explore, and develop naturally.'
    },
    {
      icon: Users,
      title: 'Early Socialization',
      description: 'From birth, our kittens are handled daily and exposed to various experiences to ensure they grow into confident, well-adjusted cats.'
    },
    {
      icon: Award,
      title: 'Responsible Placement',
      description: 'We take great care in matching kittens with the right families, ensuring each kitten goes to a loving forever home.'
    }
  ];

  return (
    <main className="min-h-screen bg-[#FAF8F5]">
      <SEO 
        title="About KittenState | Our Scottish Fold & Scottish Straight Cattery"
        description="Learn about KittenState, a family-run cattery in Covington Township, PA dedicated to raising healthy, well-socialized Scottish Fold and Scottish Straight kittens with love."
        keywords="about KittenState, Scottish Fold breeder Pennsylvania, Scottish Straight cattery, family cattery, kitten breeder"
        url="https://kittenstate.com/about"
      />

      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-rose-50 via-[#FAF8F5] to-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E85D75] mb-4">
                Our Story
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-stone-900 mb-6">
                About <span className="text-[#E85D75]">KittenState</span>
              </h1>
              <p className="text-base lg:text-lg text-stone-600 leading-relaxed mb-6">
                Welcome to KittenState, where our love for Scottish Fold and Scottish Straight
                kittens has turned into a lifelong passion. We are a small, dedicated breeder
                committed to raising healthy, well-socialized kittens in a loving home environment.
              </p>
              <p className="text-base lg:text-lg text-stone-600 leading-relaxed">
                Our journey began years ago when we fell in love with the unique charm and sweet
                temperament of Scottish Fold kittens. Since then, we've dedicated ourselves to
                ethical breeding practices that prioritize the health and happiness of every kitten.
              </p>
            </div>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl shadow-rose-100/50">
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_kitten-reserve-dev/artifacts/6d4cee6759af4596_75B70FC5-ACA2-4928-AEA9-5FA6334DB69F.png"
                  alt="Adorable Scottish Fold Kitten in Santa Costume"
                  className="w-full h-[400px] lg:h-[500px] object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-rose-100 rounded-full opacity-60 blur-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-stone-900 mb-4">
              Our Philosophy
            </h2>
            <p className="text-stone-600 max-w-2xl mx-auto">
              We believe every kitten deserves the best start in life. Here's what guides our breeding program.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((value) => {
              const IconComponent = value.icon;
              return (
                <div
                  key={value.title}
                  className="group bg-[#FAF8F5] rounded-2xl p-6 hover:shadow-lg hover:shadow-rose-100/50 transition-all duration-300"
                >
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm group-hover:bg-rose-50 transition-colors flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-[#E85D75]" />
                    </div>
                    <div>
                      <h3 className="text-lg font-serif font-semibold text-stone-800 mb-2">
                        {value.title}
                      </h3>
                      <p className="text-sm text-stone-600 leading-relaxed">
                        {value.description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Scottish Folds */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="rounded-3xl overflow-hidden shadow-xl bg-[#FAF8F5]">
                <img
                  src="https://customer-assets-jt897jd0.emergentagent.net/job_kitten-reserve-dev/artifacts/12d3cead4d96cd4f_7A514DD1-7DC5-4A12-B40B-1FAA9B126739.png"
                  alt="Adorable Scottish Fold Kitten in Elf Costume"
                  className="w-full h-[450px] lg:h-[480px] object-cover object-center"
                />
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-stone-900 mb-6">
                Why Choose Scottish Folds?
              </h2>
              <p className="text-stone-600 leading-relaxed mb-4">
                Scottish Fold kittens are known for their distinctive folded ears and sweet,
                affectionate personalities. They are gentle, adaptable cats that make wonderful
                companions for families, singles, and seniors alike.
              </p>
              <p className="text-stone-600 leading-relaxed mb-6">
                Scottish Straight kittens share the same wonderful temperament but with traditional
                straight ears. Both varieties are loving, intelligent, and make excellent indoor pets.
              </p>
              
              <div className="space-y-3">
                {['Sweet, affectionate personality', 'Adaptable to any home', 'Great with children & other pets', 'Low-maintenance grooming'].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#E85D75]" />
                    <span className="text-stone-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-rose-50 to-[#FAF8F5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-10 h-10 text-[#E85D75] mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-serif font-semibold text-stone-900 mb-6">
            Our Commitment to You
          </h2>
          <p className="text-stone-600 leading-relaxed mb-8">
            When you adopt a kitten from KittenState, you're not just getting a pet – you're
            gaining a family member who has been raised with love, care, and attention to their
            physical and emotional well-being. We are committed to transparency, ethical breeding,
            and providing lifelong support to our kitten families.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/kittens">
              <Button className="bg-[#E85D75] text-white hover:bg-[#D64560] shadow-lg shadow-[#E85D75]/25 rounded-full px-8 py-5 font-medium">
                View Our Kittens <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="bg-white text-stone-800 border-stone-200 hover:border-rose-300 rounded-full px-8 py-5 font-medium">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
